import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";

interface Company {
  id: string;
  name: string;
  slug: string | null;
  phone: string | null;
  email: string | null;
  logo_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  website: string | null;
  join_code: string | null;
  join_requests_enabled: boolean;
}

interface CompanyContextType {
  currentCompany: Company | null;
  companies: Company[];
  isLoading: boolean;
  setCurrentCompany: (company: Company) => void;
  refreshCompanies: () => Promise<void>;
  hasCompany: boolean;
  userRole: string | null;
  hasPendingRequest: boolean;
  pendingCompanyName: string | null;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [pendingCompanyName, setPendingCompanyName] = useState<string | null>(null);

  const refreshCompanies = async () => {
    if (!user) {
      setCompanies([]);
      setCurrentCompany(null);
      setIsLoading(false);
      return;
    }

    try {
      // Get all companies the user is a member of
      const { data: memberships, error: membershipError } = await supabase
        .from("company_memberships")
        .select("company_id")
        .eq("user_id", user.id)
        .eq("is_active", true);

      if (membershipError) throw membershipError;

      if (memberships && memberships.length > 0) {
        const companyIds = memberships.map((m) => m.company_id);
        
        const { data: companiesData, error: companiesError } = await supabase
          .from("companies")
          .select("*")
          .in("id", companyIds);

        if (companiesError) throw companiesError;

        setCompanies(companiesData || []);

        // Set current company to the first one if not already set
        if (!currentCompany && companiesData && companiesData.length > 0) {
          setCurrentCompany(companiesData[0]);
          
          // Get user role for this company
          const { data: roleData } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", user.id)
            .eq("company_id", companiesData[0].id)
            .single();
          
          setUserRole(roleData?.role || null);
        }
      } else {
        setCompanies([]);
        setCurrentCompany(null);

        // Demo user — skip join-request flow entirely
        const DEMO_EMAIL = "demo@kimfloraldesigns.com";
        const DEMO_COMPANY_ID = "14bdb10d-2d1a-4706-981f-ba9e2981f163";
        if (user.email === DEMO_EMAIL) {
          const { data: companyData } = await supabase
            .from("companies")
            .select("*")
            .eq("id", DEMO_COMPANY_ID)
            .single();
          if (companyData) {
            setCompanies([companyData]);
            setCurrentCompany(companyData);
            setUserRole("owner");
          }
          return;
        }

        // Check for pending join requests
        const { data: pendingReq } = await supabase
          .from("join_requests")
          .select("id, company_id")
          .eq("user_id", user.id)
          .eq("status", "pending")
          .limit(1)
          .maybeSingle();

        if (pendingReq) {
          setHasPendingRequest(true);
          const { data: companyData } = await supabase
            .from("companies")
            .select("name")
            .eq("id", pendingReq.company_id)
            .single();
          setPendingCompanyName(companyData?.name || null);
        } else {
          // Auto-create join request for the single company
          const COMPANY_ID = "14bdb10d-2d1a-4706-981f-ba9e2981f163";
          await supabase.from("join_requests").insert({
            company_id: COMPANY_ID,
            user_id: user.id,
            status: "pending",
          });
          setHasPendingRequest(true);
          const { data: companyData } = await supabase
            .from("companies")
            .select("name")
            .eq("id", COMPANY_ID)
            .single();
          setPendingCompanyName(companyData?.name || null);

          // Notify admins of auto-created join request
          try {
            await supabase.functions.invoke("notify-join-request", {
              body: {
                companyId: COMPANY_ID,
                requesterEmail: user.email || "",
              },
            });
          } catch (e) {
            console.error("Failed to send join request notification:", e);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshCompanies();
  }, [user]);

  // Update role when company changes
  useEffect(() => {
    const fetchRole = async () => {
      if (user && currentCompany) {
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .eq("company_id", currentCompany.id)
          .single();
        
        setUserRole(roleData?.role || null);
      }
    };
    fetchRole();
  }, [currentCompany, user]);

  return (
    <CompanyContext.Provider
      value={{
        currentCompany,
        companies,
        isLoading,
        setCurrentCompany,
        refreshCompanies,
        hasCompany: companies.length > 0,
        userRole,
        hasPendingRequest,
        pendingCompanyName,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
}
